export const COOKIE_CONSENT_STORAGE_KEY = 'cookie-consent';
export const COOKIE_CONSENT_CHANGED_EVENT = 'cookieConsentChanged';
export const OPEN_COOKIE_SETTINGS_EVENT = 'openCookieSettings';
export const COOKIE_CONSENT_VERSION = '2.0';
export const COOKIE_CONSENT_EXPIRY_DAYS = 365;

export interface CookieConsent {
  essential: boolean;
  preferences: boolean;
  performance: boolean;
  marketing: boolean;
  adsMeasurement: boolean;
  adsPersonalization: boolean;
  timestamp: number;
  version: string;
}

export const DEFAULT_COOKIE_CONSENT: CookieConsent = {
  essential: true,
  preferences: false,
  performance: false,
  marketing: false,
  adsMeasurement: false,
  adsPersonalization: false,
  timestamp: 0,
  version: COOKIE_CONSENT_VERSION,
};

export type ConsentGtagState = 'granted' | 'denied';

export function consentSignal(enabled: boolean): ConsentGtagState {
  return enabled ? 'granted' : 'denied';
}

export function consentAllowsGoogleTags(consent: CookieConsent | null | undefined): boolean {
  return Boolean(consent && (consent.performance || consent.marketing));
}

export function isCookieConsentCurrent(consent: CookieConsent | null | undefined): boolean {
  if (!consent || consent.version !== COOKIE_CONSENT_VERSION) {
    return false;
  }
  if (!consent.timestamp) {
    return false;
  }
  const ageMs = Date.now() - consent.timestamp;
  return ageMs >= 0 && ageMs < COOKIE_CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
}

export function parseStoredCookieConsent(raw: string | null): CookieConsent | null {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as CookieConsent;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    return {
      essential: true,
      preferences: Boolean(parsed.preferences),
      performance: Boolean(parsed.performance),
      marketing: Boolean(parsed.marketing),
      adsMeasurement: Boolean(parsed.adsMeasurement),
      adsPersonalization: Boolean(parsed.adsPersonalization),
      timestamp: Number(parsed.timestamp) || 0,
      version: typeof parsed.version === 'string' ? parsed.version : '',
    };
  } catch {
    return null;
  }
}

export function readStoredCookieConsent(): CookieConsent | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return parseStoredCookieConsent(localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function buildCookieConsent(
  input: Pick<
    CookieConsent,
    'preferences' | 'performance' | 'marketing' | 'adsMeasurement' | 'adsPersonalization'
  >,
): CookieConsent {
  const marketing = input.marketing || input.adsMeasurement || input.adsPersonalization;
  return {
    essential: true,
    preferences: Boolean(input.preferences),
    performance: input.performance,
    marketing,
    adsMeasurement: marketing ? input.adsMeasurement : false,
    adsPersonalization: marketing ? input.adsPersonalization : false,
    timestamp: Date.now(),
    version: COOKIE_CONSENT_VERSION,
  };
}

export function pushConsentToGtag(consent: CookieConsent): void {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  window.gtag('consent', 'update', {
    ad_storage: consentSignal(consent.marketing),
    ad_user_data: consentSignal(consent.adsMeasurement),
    ad_personalization: consentSignal(consent.adsPersonalization),
    analytics_storage: consentSignal(consent.performance),
    functionality_storage: consentSignal(consent.preferences),
    personalization_storage: consentSignal(consent.adsPersonalization),
    security_storage: 'granted',
  });
}

export function persistCookieConsent(consent: CookieConsent): CookieConsent {
  const stored = {
    ...consent,
    essential: true as const,
    timestamp: Date.now(),
    version: COOKIE_CONSENT_VERSION,
  };
  if (typeof window !== 'undefined') {
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(stored));
    pushConsentToGtag(stored);
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_CHANGED_EVENT, { detail: stored }));
  }
  return stored;
}

export function openCookiePreferences(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new CustomEvent(OPEN_COOKIE_SETTINGS_EVENT));
}
